import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { getVendorDirectoryTaxonomy } from "../../api/clientVendorDirectoryApi";
import { listVendorDirectoryCandidates } from "../../api/clientVendorDirectoryImportApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const CANDIDATE_STATUS_FILTERS = [
  "",
  "new",
  "ready_for_review",
  "possible_duplicate",
  "deferred",
  "rejected",
  "accepted",
];

function formatCandidateDate(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function candidateStatusBadgeClass(status) {
  if (status === "accepted") return "cvd-badge--published";
  if (status === "rejected") return "cvd-badge--suspended";
  if (["possible_duplicate", "deferred", "ready_for_review"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

export default function ClientVendorDirectoryCandidateListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const navigate = useNavigate();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") || "";
  const search = searchParams.get("search") || "";
  const importJobId = searchParams.get("importJobId") || "";
  const collectionRunId = searchParams.get("collectionRunId") || "";
  const page = Number(searchParams.get("page") || 1);
  const basePath = `${serviciosBase}/candidatos`;
  const copyPrefix = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.candidates";

  const categoryByKey = useMemo(() => {
    const map = new Map();
    (taxonomy?.categories || []).forEach((category) => {
      if (category.key) map.set(category.key, category);
    });
    return map;
  }, [taxonomy]);

  const municipalityById = useMemo(() => {
    const map = new Map();
    (taxonomy?.municipalities || []).forEach((municipality) => {
      if (municipality.id) map.set(municipality.id, municipality);
    });
    return map;
  }, [taxonomy]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [body, taxonomyBody] = await Promise.all([
        listVendorDirectoryCandidates(clientViewId, {
          page,
          pageSize: 25,
          status: status || undefined,
          search: search || undefined,
          importJobId: importJobId || undefined,
          collectionRunId: collectionRunId || undefined,
        }),
        getVendorDirectoryTaxonomy(clientViewId, { locale }),
      ]);
      setData(body);
      setTaxonomy(taxonomyBody.taxonomy || taxonomyBody);
    } catch (error) {
      setErrorMessage(error.message || t(`${copyPrefix}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, collectionRunId, importJobId, locale, page, search, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const viewName = activeView?.name || "CasaMapa";
  const candidates = data?.candidates || [];

  const openCandidate = (candidateId) => {
    navigate(`${basePath}/${candidateId}`);
  };

  return (
    <div className="page-content cvd-admin cvd-candidate-queue">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${copyPrefix}.title`)}</h1>
          <p className="lede">{t(`${copyPrefix}.intro`, { viewName })}</p>
        </div>
      </div>

      {collectionRunId ? (
        <div className="cvd-alert cvd-alert--warning" role="status">
          {t(`${copyPrefix}.collectionRunFilterHint`, { runId: collectionRunId })}
        </div>
      ) : null}

      <aside className="cvd-candidate-queue__workflow" aria-label={t(`${copyPrefix}.listWorkflowTitle`)}>
        <strong>{t(`${copyPrefix}.listWorkflowTitle`)}</strong>
        <p>{t(`${copyPrefix}.listWorkflowHint`)}</p>
      </aside>

      <div className="cvd-filters">
        <div className="cvd-field">
          <label htmlFor="candidate-search">{t(`${copyPrefix}.searchLabel`)}</label>
          <input
            id="candidate-search"
            type="search"
            value={search}
            onChange={(e) => updateParam("search", e.target.value)}
            placeholder={t(`${copyPrefix}.searchPlaceholder`)}
          />
        </div>
        <div className="cvd-field">
          <label htmlFor="candidate-status">{t(`${copyPrefix}.statusLabel`)}</label>
          <select
            id="candidate-status"
            value={status}
            onChange={(e) => updateParam("status", e.target.value)}
          >
            {CANDIDATE_STATUS_FILTERS.map((value) => (
              <option key={value || "all"} value={value}>
                {value
                  ? t(`${copyPrefix}.status.${value}`)
                  : t(`${copyPrefix}.allStatuses`)}
              </option>
            ))}
          </select>
        </div>
        <div className="cvd-field">
          <label htmlFor="candidate-import">{t(`${copyPrefix}.importLabel`)}</label>
          <input
            id="candidate-import"
            value={importJobId}
            onChange={(e) => updateParam("importJobId", e.target.value)}
            placeholder={t(`${copyPrefix}.importPlaceholder`)}
          />
        </div>
      </div>

      {loading && <ClientPortalLoading message={t(`${copyPrefix}.loading`)} />}
      {!loading && errorMessage && <ClientPortalError message={errorMessage} onRetry={load} />}
      {!loading && !errorMessage && (
        <div className="cvd-table-wrap">
          <table className="cvd-table cvd-candidate-queue-table">
            <thead>
              <tr>
                <th>{t(`${copyPrefix}.table.name`)}</th>
                <th>{t(`${copyPrefix}.table.status`)}</th>
                <th className="cvd-table__col--hide-mobile">{t(`${copyPrefix}.table.category`)}</th>
                <th className="cvd-table__col--hide-mobile">{t(`${copyPrefix}.table.municipality`)}</th>
                <th className="cvd-table__col--hide-mobile">{t(`${copyPrefix}.table.firstSeen`)}</th>
                <th className="cvd-table__col--actions">{t(`${copyPrefix}.table.actions`)}</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    {collectionRunId
                      ? t(`${copyPrefix}.collectionRunFilterEmpty`)
                      : t(`${copyPrefix}.empty`)}
                  </td>
                </tr>
              ) : (
                candidates.map((candidate) => {
                  const detailPath = `${basePath}/${candidate.id}`;
                  const categoryLabel =
                    categoryByKey.get(candidate.suggestedCategoryKey)?.name ||
                    candidate.suggestedCategoryName ||
                    candidate.suggestedCategoryKey ||
                    "—";
                  const municipalityLabel =
                    municipalityById.get(candidate.suggestedMunicipalityId)?.name ||
                    candidate.suggestedMunicipalityName ||
                    candidate.rawMunicipality ||
                    "—";
                  const statusLabel = candidate.status
                    ? t(`${copyPrefix}.status.${candidate.status}`, {}, candidate.status)
                    : "—";
                  const canDecide = !["accepted", "rejected"].includes(candidate.status);

                  return (
                    <tr
                      key={candidate.id}
                      className="cvd-candidate-queue-table__row"
                      tabIndex={0}
                      onClick={() => openCandidate(candidate.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openCandidate(candidate.id);
                        }
                      }}
                    >
                      <td>
                        <Link
                          to={detailPath}
                          className="cvd-candidate-queue-table__name"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {candidate.rawName || candidate.displayName || "—"}
                        </Link>
                      </td>
                      <td>
                        <span className={`cvd-badge ${candidateStatusBadgeClass(candidate.status)}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="cvd-table__col--hide-mobile">{categoryLabel}</td>
                      <td className="cvd-table__col--hide-mobile">{municipalityLabel}</td>
                      <td className="cvd-table__col--hide-mobile">
                        {formatCandidateDate(candidate.firstSeenAt, locale)}
                      </td>
                      <td className="cvd-table__col--actions">
                        <Link
                          to={detailPath}
                          className={`cvd-btn ${canDecide ? "cvd-btn--primary" : "cvd-btn--secondary"}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {canDecide ? t(`${copyPrefix}.reviewAndDecide`) : t(`${copyPrefix}.review`)}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
